/**
 * CARLA HOME Cart Drawer — Full JS (Camino B)
 * Web Components: carlahome-cart-drawer, carlahome-cart-drawer-items
 * Replaces Dawn's cart-drawer.js entirely
 */

/* ═══════════════════════════════════════════
   1. CART DRAWER — Main container
   ═══════════════════════════════════════════ */
class CarlaHomeCartDrawer extends HTMLElement {
  constructor() {
    super();

    this.overlay = this.querySelector('#CartDrawer-Overlay');
    this.panel = this.querySelector('#CartDrawer');
    this.activeElement = null;
    this._isRefreshing = false;

    /* Close triggers */
    this.addEventListener('keyup', (e) => e.code === 'Escape' && this.close());
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    /* Header cart icon binding */
    this.bindCartIcon();

    /* Discount code redirect */
    this.bindDiscountInput();

    /* Abrir drawer cuando product-form.js publica cartUpdate */
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
        if (event.source === 'carlahome-cart-drawer') return;
        if (event.source === 'cart-items') return;
        await this.refreshDrawer();
        if (!this.classList.contains('active')) {
          this.open(null);
        }
      });
    }

    /* Quantity +/- buttons (delegated to drawer, not items) */
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('.ch-cart-item__qty-btn');
      if (!btn) return;
      e.preventDefault();
      const qtyWrap = btn.closest('.ch-cart-item__qty');
      if (!qtyWrap) return;
      const input = qtyWrap.querySelector('.ch-cart-item__qty-input');
      if (!input) return;
      const current = parseInt(input.value, 10) || 0;
      const index = input.dataset.index;

      if (btn.getAttribute('name') === 'minus') {
        this.updateQuantity(index, Math.max(0, current - 1));
      } else if (btn.getAttribute('name') === 'plus') {
        this.updateQuantity(index, current + 1);
      }
    });

    /* Remove button X — delegated */
    this.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('cart-remove-button');
      if (!removeBtn) return;
      e.preventDefault();
      const index = removeBtn.dataset.index;
      if (index) this.updateQuantity(index, 0);
    });
  }

  /* ── Header icon ── */
  bindCartIcon() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    if (cartLink._chBound) return;
    cartLink._chBound = true;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (e) => {
      e.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (e) => {
      if (e.code === 'Space' || e.code === 'Enter') {
        e.preventDefault();
        this.open(cartLink);
      }
    });
  }

  /* ── Open ── */
  open(triggeredBy) {
    if (triggeredBy) this.activeElement = triggeredBy;

    /* Prevent body scroll */
    this._scrollY = window.scrollY;
    document.body.style.top = `-${this._scrollY}px`;
    document.body.classList.add('overflow-hidden');

    /* Animate in */
    requestAnimationFrame(() => {
      this.classList.add('active');
    });

    /* Focus trap */
    this.addEventListener('transitionend', () => {
      const target = this.classList.contains('is-empty')
        ? this.querySelector('.ch-cart-empty')
        : this.panel;
      if (target) {
        const focusEl = target.querySelector('button, a, input') || target;
        focusEl.focus();
      }
    }, { once: true });

    /* Load upsell recommendations */
    this.loadUpsell();

    /* GA4 view_cart */
    this.trackViewCart();
  }

  /* ── GA4 view_cart tracking ── */
  async trackViewCart() {
    if (typeof gtag !== 'function') return;
    try {
      const cart = await fetch('/cart.js').then((r) => r.json());
      if (!cart.item_count) return;
      gtag('event', 'view_cart', {
        currency: cart.currency,
        value: cart.total_price / 100,
        items: cart.items.map((item, i) => ({
          item_id: item.sku || item.variant_id,
          item_name: item.product_title,
          item_variant: item.variant_title,
          price: item.price / 100,
          quantity: item.quantity,
          index: i,
        })),
      });
    } catch (err) {
      console.error('CARLA HOME Cart Drawer: view_cart tracking failed', err);
    }
  }

  /* ── Close ── */
  close() {
    this.classList.remove('active');

    /* Restore body scroll */
    document.body.classList.remove('overflow-hidden');
    document.body.style.top = '';
    window.scrollTo(0, this._scrollY || 0);

    /* Return focus */
    if (this.activeElement) {
      this.activeElement.focus();
      this.activeElement = null;
    }
  }

  /* ── Discount code → checkout redirect ── */
  bindDiscountInput() {
    const applyBtn = this.querySelector('[data-discount-apply]');
    const input = this.querySelector('#ch-discount-code');
    if (!applyBtn || !input) return;

    const applyDiscount = () => {
      const code = input.value.trim();
      if (!code) return;
      window.location.href = `/checkout?discount=${encodeURIComponent(code)}`;
    };

    applyBtn.addEventListener('click', applyDiscount);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        applyDiscount();
      }
    });
  }

  /* ── Upsell: Recommendations API ── */
  async loadUpsell() {
    const container = this.querySelector('[data-upsell-list]');
    if (!container) return;

    /* Get first cart item product ID */
    const firstItem = this.querySelector('.ch-cart-item');
    if (!firstItem) {
      container.closest('.ch-cart-upsell')?.classList.add('hidden');
      return;
    }

    /* Extract product_id from the item URL: /products/handle */
    const itemLink = firstItem.querySelector('.ch-cart-item__media');
    if (!itemLink) return;

    const handle = itemLink.getAttribute('href')?.split('/products/')[1]?.split('?')[0];
    if (!handle) return;

    try {
      /* First get product ID from handle */
      const productRes = await fetch(`/products/${handle}.js`);
      if (!productRes.ok) throw new Error('Product fetch failed');
      const productData = await productRes.json();
      const productId = productData.id;
      if (!productId) return;

      /* Get cart item IDs to exclude */
      const cartItemIds = new Set();
      this.querySelectorAll('.ch-cart-item .ch-cart-item__media').forEach(link => {
        const h = link.getAttribute('href')?.split('/products/')[1]?.split('?')[0];
        if (h) cartItemIds.add(h);
      });

      /* Fetch recommendations */
      const recRes = await fetch(`/recommendations/products.json?product_id=${productId}&limit=10&intent=complementary`);
      if (!recRes.ok) throw new Error('Recommendations fetch failed');
      const recData = await recRes.json();

      let products = (recData.products || []).filter(p => !cartItemIds.has(p.handle));

      if (products.length === 0) {
        /* Fallback: try related intent */
        const fallbackRes = await fetch(`/recommendations/products.json?product_id=${productId}&limit=10&intent=related`);
        if (fallbackRes.ok) {
          const fallbackData = await fallbackRes.json();
          products = (fallbackData.products || []).filter(p => !cartItemIds.has(p.handle));
        }
      }

      if (products.length === 0) {
        container.closest('.ch-cart-upsell')?.classList.add('hidden');
        return;
      }

      /* Render upsell cards */
      container.innerHTML = products.map(product => this.renderUpsellCard(product)).join('');

      /* Bind variant selectors and ATC buttons */
      container.querySelectorAll('.ch-upsell-card').forEach(card => {
        this.bindUpsellCard(card);
      });

    } catch (err) {
      console.warn('CARLA HOME Cart Drawer: Upsell load failed', err);
      container.closest('.ch-cart-upsell')?.classList.add('hidden');
    }
  }

  /* ── Render single upsell card ── */
  renderUpsellCard(product) {
    const firstVariant = product.variants[0];
    const hasMultipleVariants = product.variants.length > 1;
    const hasCompare = firstVariant.compare_at_price && firstVariant.compare_at_price > firstVariant.price;
    const savingsPct = hasCompare
      ? Math.round((1 - firstVariant.price / firstVariant.compare_at_price) * 100)
      : 0;

    const imgSrc = product.featured_image
      ? this.getSizedImageUrl(product.featured_image, '200x')
      : '';

    /* Build variant options HTML */
    let variantSelectHtml = '';
    if (hasMultipleVariants) {
      const options = product.variants.map(v =>
        `<option value="${v.id}" data-price="${v.price}" data-compare="${v.compare_at_price || ''}" data-available="${v.available}">${v.title}</option>`
      ).join('');
      variantSelectHtml = `
        <div class="ch-upsell-card__variant-wrap">
          <label class="visually-hidden" for="ch-upsell-variant-${product.id}">Select variant</label>
          <select class="ch-upsell-card__variant-select" id="ch-upsell-variant-${product.id}" data-upsell-variant-select>
            ${options}
          </select>
        </div>`;
    }

    return `
      <article class="ch-upsell-card" data-product-id="${product.id}" data-variant-id="${firstVariant.id}">
        <div class="ch-upsell-card__row">
          <div class="ch-upsell-card__media">
            <a href="${product.url}" tabindex="-1" aria-hidden="true">
              ${imgSrc ? `<img src="${imgSrc}" alt="${this.escapeHtml(product.title)}" width="80" height="100" loading="lazy">` : ''}
            </a>
            ${savingsPct > 0 ? `<span class="ch-upsell-card__badge">${savingsPct}% OFF</span>` : ''}
          </div>
          <div class="ch-upsell-card__info">
            <a href="${product.url}" class="ch-upsell-card__title">${this.escapeHtml(product.title)}</a>
            <div class="ch-upsell-card__prices" data-upsell-prices>
              <span class="ch-upsell-card__price">${this.formatMoney(firstVariant.price)}</span>
              ${hasCompare ? `<span class="ch-upsell-card__price-compare">${this.formatMoney(firstVariant.compare_at_price)}</span>` : ''}
            </div>
            ${variantSelectHtml}
          </div>
        </div>
        <button type="button" class="ch-upsell-card__atc" data-upsell-atc ${!firstVariant.available ? 'disabled' : ''}>
          ${firstVariant.available ? 'Add to Cart' : 'Sold Out'}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
            <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
            <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/>
          </svg>
        </button>
      </article>`;
  }

  /* ── Bind upsell card interactions ── */
  bindUpsellCard(card) {
    const select = card.querySelector('[data-upsell-variant-select]');
    const atcBtn = card.querySelector('[data-upsell-atc]');
    const pricesEl = card.querySelector('[data-upsell-prices]');

    /* Variant change → update price, availability, selected variant ID */
    if (select) {
      select.addEventListener('change', () => {
        const option = select.options[select.selectedIndex];
        const price = parseInt(option.dataset.price, 10);
        const compare = option.dataset.compare ? parseInt(option.dataset.compare, 10) : null;
        const available = option.dataset.available === 'true';

        card.dataset.variantId = option.value;

        /* Update prices */
        if (pricesEl) {
          let html = `<span class="ch-upsell-card__price">${this.formatMoney(price)}</span>`;
          if (compare && compare > price) {
            html += `<span class="ch-upsell-card__price-compare">${this.formatMoney(compare)}</span>`;
          }
          pricesEl.innerHTML = html;
        }

        /* Update badge */
        const badge = card.querySelector('.ch-upsell-card__badge');
        if (compare && compare > price) {
          const pct = Math.round((1 - price / compare) * 100);
          if (badge) {
            badge.textContent = `${pct}% OFF`;
            badge.classList.remove('hidden');
          }
        } else if (badge) {
          badge.classList.add('hidden');
        }

        /* Update ATC button */
        if (atcBtn) {
          atcBtn.disabled = !available;
          atcBtn.innerHTML = available
            ? 'Add to Cart <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/></svg>'
            : 'Sold Out';
        }
      });
    }

    /* ATC → add variant to cart via /cart/add.js */
    if (atcBtn) {
      atcBtn.addEventListener('click', async () => {
        const variantId = card.dataset.variantId;
        if (!variantId || atcBtn.disabled) return;

        atcBtn.disabled = true;
        atcBtn.textContent = 'Adding...';

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
          });

          if (!res.ok) throw new Error('Add to cart failed');

          /* Refresh the entire drawer via section rendering */
          await this.refreshDrawer();

          /* Notify other components */
          if (typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'carlahome-cart-drawer' });
          }

        } catch (err) {
          console.error('CARLA HOME Cart Drawer: ATC failed', err);
          atcBtn.textContent = 'Error — try again';
          atcBtn.disabled = false;
        }
      });
    }
  }

  /* ── Refresh drawer content via section rendering ── */
  async refreshDrawer() {
    if (this._isRefreshing) {
      // Return existing promise so callers can await the same refresh
      return this._refreshPromise || Promise.resolve();
    }
    this._isRefreshing = true;
    this._refreshPromise = this._doRefresh();
    try {
      await this._refreshPromise;
    } finally {
      this._isRefreshing = false;
      this._refreshPromise = null;
    }
  }

  async _doRefresh() {
    try {
      const [drawerRes, bubbleRes] = await Promise.all([
        fetch('/cart?section_id=cart-drawer'),
        fetch('/cart?section_id=cart-icon-bubble')
      ]);

      if (drawerRes.ok) {
        const text = await drawerRes.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const newDrawer = doc.querySelector('carlahome-cart-drawer');

        if (newDrawer) {
          /* Swap inner content of header, body, footer */
          const newHeader = newDrawer.querySelector('.ch-cart-header');
          const newBody = newDrawer.querySelector('.ch-cart-body');
          const newFooter = newDrawer.querySelector('.ch-cart-footer');

          const curHeader = this.querySelector('.ch-cart-header');
          const curBody = this.querySelector('.ch-cart-body');
          const curFooter = this.querySelector('.ch-cart-footer');

          if (newHeader && curHeader) {
            const newCount = newHeader.querySelector('.ch-cart-header__count');
            const curCount = curHeader.querySelector('.ch-cart-header__count');
            if (newCount && curCount) curCount.textContent = newCount.textContent;

            const newShipping = newHeader.querySelector('.ch-cart-shipping');
            const curShipping = curHeader.querySelector('.ch-cart-shipping');
            if (newShipping && curShipping) curShipping.innerHTML = newShipping.innerHTML;
          }
          if (newBody && curBody) curBody.innerHTML = newBody.innerHTML;
          if (newFooter && curFooter) {
            curFooter.innerHTML = newFooter.innerHTML;
            curFooter.className = newFooter.className;
          }

          /* Re-bind discount input (new DOM) */
          this.bindDiscountInput();

          /* Update empty state class */
          const isEmpty = newDrawer.classList.contains('is-empty');
          this.classList.toggle('is-empty', isEmpty);

          /* Show/hide items and footer based on empty state */
          const itemsEl = this.querySelector('carlahome-cart-drawer-items');
          const emptyEl = this.querySelector('.ch-cart-empty');

          if (isEmpty) {
            if (itemsEl) itemsEl.classList.add('hidden');
            if (emptyEl) emptyEl.classList.remove('hidden');
            if (curFooter) curFooter.classList.add('hidden');
          } else {
            if (itemsEl) itemsEl.classList.remove('hidden');
            if (emptyEl) emptyEl.classList.add('hidden');
            if (curFooter) curFooter.classList.remove('hidden');
          }

          /* Reload upsell */
          this.loadUpsell();
        }
      }

      if (bubbleRes.ok) {
        const text = await bubbleRes.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const newBubbleBtn = doc.querySelector('#cart-icon-bubble');
        const oldBubbleBtn = document.getElementById('cart-icon-bubble');
        if (newBubbleBtn && oldBubbleBtn) {
          // Only update the count badge, not the whole button — preserve event listeners
          const newCount = newBubbleBtn.querySelector('.ch-cart-count, .cart-count-bubble, [data-cart-count]');
          const oldCount = oldBubbleBtn.querySelector('.ch-cart-count, .cart-count-bubble, [data-cart-count]');
          if (newCount && oldCount) {
            oldCount.textContent = newCount.textContent;
            oldCount.className = newCount.className;
          }
          // Update aria-label with new count
          if (newBubbleBtn.getAttribute('aria-label')) {
            oldBubbleBtn.setAttribute('aria-label', newBubbleBtn.getAttribute('aria-label'));
          }
        }
      }
    } catch (err) {
      console.error('CARLA HOME Cart Drawer: Refresh failed', err);
    }
  }

  /* ── Update quantity ── */
  async updateQuantity(line, quantity) {
    const lineItem = document.getElementById(`CartDrawer-Item-${line}`);

    /* Show spinner */
    if (lineItem) {
      const spinner = lineItem.querySelector('.loading__spinner');
      if (spinner) spinner.classList.remove('hidden');
    }

    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: parseInt(line, 10), quantity: parseInt(quantity, 10) })
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const state = await res.json();

      if (state.errors) {
        const errorEl = document.getElementById(`CartDrawer-LineItemError-${line}`);
        if (errorEl) {
          const textEl = errorEl.querySelector('.cart-item__error-text');
          if (textEl) textEl.textContent = state.errors;
        }
        if (lineItem) {
          const spinner = lineItem.querySelector('.loading__spinner');
          if (spinner) spinner.classList.add('hidden');
        }
        return;
      }

      /* Refresh drawer with new server-rendered HTML */
      await this.refreshDrawer();

      /* Publish update event for other components */
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state });
      }

    } catch (err) {
      console.error('CARLA HOME Cart Drawer: Quantity update failed', err);
      if (lineItem) {
        const spinner = lineItem.querySelector('.loading__spinner');
        if (spinner) spinner.classList.add('hidden');
      }
    }
  }

  /* ── Section rendering contract (for cart.js compatibility) ── */
  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' }
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  async renderContents(parsedState) {
    await this.refreshDrawer();
    if (!this.classList.contains('active')) {
      this.open(null);
    }
  }

  /* ── Utility: format Shopify price (cents → $X.XX) ── */
  formatMoney(cents) {
    if (typeof cents !== 'number') return '';
    return `$${(cents / 100).toFixed(2)}`;
  }

  /* ── Utility: get sized Shopify image URL ── */
  getSizedImageUrl(url, size) {
    if (!url) return '';
    const match = url.match(/\.(jpg|jpeg|png|gif|webp)(\?|$)/i);
    if (match) {
      return url.replace(match[0], `_${size}.${match[1]}${match[2]}`);
    }
    return url;
  }

  /* ── Utility: escape HTML ── */
  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

customElements.define('carlahome-cart-drawer', CarlaHomeCartDrawer);


/* ═══════════════════════════════════════════
   2. CART DRAWER ITEMS — Extends Dawn CartItems
   ═══════════════════════════════════════════ */
class CarlaHomeCartDrawerItems extends HTMLElement {
  constructor() {
    super();
    const debouncedOnChange = debounce((event) => { this.onChange(event); }, ON_CHANGE_DEBOUNCE_TIMER);
    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  onChange(event) {
    const input = event.target;
    if (!input.dataset.index) return;
    const drawer = this.closest('carlahome-cart-drawer');
    if (drawer) drawer.updateQuantity(input.dataset.index, input.value);
  }
}

customElements.define('carlahome-cart-drawer-items', CarlaHomeCartDrawerItems);

/* ═══════════════════════════════════════════
   3. AUTO-OPEN — /cart redirige aqui con ?open_cart=1
   ═══════════════════════════════════════════ */
if (new URLSearchParams(window.location.search).has('open_cart')) {
  document.addEventListener('DOMContentLoaded', async () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('open_cart');
    window.history.replaceState({}, '', url);

    const drawer = document.querySelector('carlahome-cart-drawer');
    if (!drawer) return;
    if (typeof drawer.open !== 'function') {
      await customElements.whenDefined('carlahome-cart-drawer');
    }
    drawer.open(null);
  });
}
